// R-FIN-POWER-STACK — Power-semantics FOUNDATION.
//
// A datacenter's "MW" is ambiguous unless you state the BASIS. This module is
// the single source of truth that resolves an entered MW figure into the full
// power stack:
//
//   IT load  ──×PUE──▶  Facility load  ≈  Grid demand
//                                  │
//                                  ├──÷PF──▶  Apparent power (MVA)  ← what
//                                  │          transformers / switchgear are
//                                  │          RATED in
//                                  │
//                                  └──÷(1−T&D)──▶  Generation required (MW)
//
// The entered MW (`ProjectIntent.powerRequirementMW` / `phase.capacityMW`) is
// NEVER mutated — the basis tells us which LAYER it sits on, and we derive the
// rest. This file only supplies the resolver; downstream propagation (LCOE,
// generation economics, sizing UI) happens in a later pass.
//
// CUMULATIVE convention (site-owner decision 2026-06-08): `phase.capacityMW` is
// the TOTAL (cumulative) site capacity AT that phase's completion. For the
// power-stack resolver that is exactly the right input — at phase i the campus
// (and therefore its FACILITY LOAD) is the cumulative size, so the entered MW
// is used as-is per phase (no differencing here). Per-phase INCREMENTS
// (capacity ADDED) are only needed by the CAPEX / energy / BoM consumers, which
// difference via `phaseIncrementMW` (projectIntent.ts) — NOT here.
//
// UNITS: MW (real power), MVA (apparent power), PUE / PF / % dimensionless.
// Pure planning arithmetic — no CoolProp / IAPWS boundary here.

import type {
  PhasePowerComponentKey,
  PowerSourceKey,
  ProjectIntent,
  ProjectPhase,
} from '../types/siteRag';
import {
  POWER_BUCKET_LABEL,
  SIZING_BASIS_RULE,
  bucketMidpointMw,
  bucketMwRange,
  effectivePhases,
  effectivePower,
  effectivePowerMwRange,
} from './projectIntent';
import type { PowerBucket, SizingBasisRule } from './projectIntent';

/**
 * Default Power Usage Effectiveness (facility ÷ IT).
 *
 * Uptime Institute's 2024 Global Data Center Survey puts the industry AVERAGE
 * at ~1.56; hyperscale fleets reach ~1.2; a modern new-build lands near 1.3.
 * We default to a modern-build 1.3 (conservative vs hyperscale, optimistic vs
 * the legacy-heavy industry average) and let the user override per project.
 * Ref: Uptime Institute Global Data Center Survey 2024.
 */
export const DEFAULT_PUE = 1.3;

/**
 * Default power factor (real ÷ apparent).
 *
 * Modern datacenter UPS / PFC front-ends hold ~0.9–1.0 at the meter; 0.95 is a
 * defensible planning default. Used to convert real MW to apparent MVA, the
 * basis transformers and switchgear are rated in.
 * Ref: typical modern UPS / power-factor-corrected load specs.
 */
export const DEFAULT_POWER_FACTOR = 0.95;

/** Physical domain checks shared by intent inputs and all power projections. */
export function isValidPue(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 1;
}

export function isValidPowerFactor(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 1;
}

/**
 * Default transmission & distribution loss (%).
 *
 * EIA reports ~5% average T&D losses on the U.S. grid between net generation
 * and delivered electricity. T&D loss is OUTSIDE the facility meter, so it
 * scales the DELIVERED facility load up to the GENERATION that must be produced
 * to deliver it — it does NOT compound with PUE (PUE losses are INSIDE the
 * facility meter; see `resolvePowerStack`).
 * Ref: U.S. EIA — electricity T&D losses ~5%.
 */
export const DEFAULT_TD_LOSS_PCT = 5;

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG-AWARE PUE (R-FIN-POWER-STACK) — estimate a project's PUE from its
// declared cooling configuration when the user has not given an explicit value.
//
// PUE = facility power ÷ IT power. The dominant non-IT term is COOLING, so the
// cooling strategy is the strongest single predictor of PUE. The base figures
// below are Vertiv's cooling-architecture PUE benchmarks (dimensionless):
//
//   air (room/CRAH, no economisation) .......... ~1.5
//   evaporative (wet tower / adiabatic) ......... ~1.25
//   liquid / direct-to-chip (rear-door, D2C) .... ~1.3
//   immersion (single/two-phase) ................ ~1.08
//
// (Ref: Vertiv — "PUE by cooling architecture". Air is the legacy default;
// immersion is the lowest-overhead liquid approach.) For combinations and the
// not-yet-decided case we apply documented planning defaults (RULE 5):
//
//   hybrid (mixed air + liquid) ................. 1.35  (between air 1.5 & liquid 1.3,
//                                                  weighted toward the air leg)
//   undecided / unknown ......................... 1.4   (conservative — closer to the
//                                                  legacy-heavy industry mix; flagged
//                                                  an estimate, not a commitment)
//
// These are ESTIMATES used only when nothing better is known; an explicit
// `pueValue` / `pueTarget` / adopted-simulation PUE always wins (see
// `resolvePueWithSource`).
// ─────────────────────────────────────────────────────────────────────────────

/** Vertiv cooling-architecture PUE benchmark — room/air cooling. */
export const CONFIG_PUE_AIR = 1.5;
/** Vertiv cooling-architecture PUE benchmark — evaporative (wet) cooling. */
export const CONFIG_PUE_EVAPORATIVE = 1.25;
/** Vertiv cooling-architecture PUE benchmark — liquid / direct-to-chip. */
export const CONFIG_PUE_LIQUID = 1.3;
/** Vertiv cooling-architecture PUE benchmark — immersion (lowest overhead). */
export const CONFIG_PUE_IMMERSION = 1.08;
/** Planning default — hybrid (mixed air + liquid). Documented, RULE 5. */
export const CONFIG_PUE_HYBRID = 1.35;
/** Conservative planning default — cooling undecided / unknown. Documented, RULE 5. */
export const CONFIG_PUE_UNDECIDED = 1.4;

/**
 * BTM waste-heat OFFSET fraction — the share of the cooling-electrical OVERHEAD
 * (the PUE "− 1" term) that is offloaded when on-site thermal generation drives
 * absorption / CCHP cooling, making cooling a THERMAL rather than electrical
 * load. Documented planning default (RULE 5): absorption chillers commonly take
 * over roughly HALF of the cooling-electrical demand on a heat-rich BTM site
 * (Araner / Clarke Energy report >20% primary-energy savings for CCHP). Applied
 * as `1 + (basePUE − 1) × (1 − OFFSET)`, floored at `CONFIG_PUE_FLOOR`.
 */
export const WASTE_HEAT_OFFSET = 0.5;

/**
 * Hard floor for any config-derived (and waste-heat-credited) PUE. A facility
 * cannot reach PUE 1.0 (no zero-overhead facility exists); 1.05 is below the
 * best real immersion builds and guards the credit arithmetic from producing a
 * non-physical PUE. Documented planning default (RULE 5).
 */
export const CONFIG_PUE_FLOOR = 1.05;

/**
 * `PowerSourceKey`s that denote ON-SITE THERMAL generation — combustion or
 * fission with a high-grade heat stream that can drive absorption / CCHP.
 * (Grid, solar+BESS, wind+BESS carry no usable on-site heat.) `hybrid` is
 * resolved by inspecting the phase's granular `powerSourceConfig.components`.
 */
const ON_SITE_THERMAL_SOURCE_KEYS: ReadonlySet<PowerSourceKey> = new Set([
  'gas-cc',
  'gas-sc',
  'smr',
  'large-nuclear',
]);

/**
 * Granular `PhasePowerComponentKey`s that denote ON-SITE THERMAL generation.
 * `recip` and `gas-turbine` are the granular forms of gas-sc / gas-cc.
 */
const ON_SITE_THERMAL_COMPONENT_KEYS: ReadonlySet<PhasePowerComponentKey> = new Set([
  'recip',
  'gas-turbine',
  'smr',
  'large-nuclear',
]);

/** True when this phase has any on-site thermal generation (coarse or granular). */
function phaseHasOnSiteThermalGen(phase: ProjectPhase): boolean {
  if (ON_SITE_THERMAL_SOURCE_KEYS.has(phase.powerSource)) return true;
  const components = phase.powerSourceConfig?.components ?? [];
  if (components.some(c => ON_SITE_THERMAL_COMPONENT_KEYS.has(c))) return true;
  if (components.includes('fuel-cell')) {
    const fc = phase.powerSourceConfig?.fuelCellConfig;
    if ((fc?.chpUsefulHeatRecoveryPct ?? 0) > 0 || fc?.chemistry === 'pafc' || fc?.chemistry === 'mcfc') {
      return true;
    }
  }
  return false;
}

/** True when ANY phase of the intent has on-site thermal generation. */
function intentHasOnSiteThermalGen(intent: ProjectIntent | undefined): boolean {
  if (!intent) return false;
  const phases = effectivePhases(intent).phases;
  return phases.some(phaseHasOnSiteThermalGen);
}

// ─────────────────────────────────────────────────────────────────────────────
// COOLING-GENERATION machine (#157) — the COOLING-GENERATION side, distinct from
// both the IT-side `coolingStrategy` (air / liquid / …) AND the cold-end SINK
// (`bopHeatRejection`: dry-cooler / wet-tower / …). This names the MACHINE that
// produces the cooling effect:
//
//   - `absorption-thermal`        — absorption / CCHP chiller driven by on-site
//     thermal heat → cooling is a THERMAL (not electrical) load → heat-usable.
//   - `steam-driven-mechanical`   — steam-turbine-driven mechanical chiller fed
//     by on-site high-grade steam → also offloads cooling-electrical demand →
//     heat-usable.
//   - `vapour-compression-electric` — conventional electric chiller. ADDS
//     electrical load; earns NO waste-heat credit (correct physics).
//
// The BTM waste-heat PUE credit must gate on this MACHINE — NOT on the sink
// enum — so that an electric chiller never earns the credit and a thermal
// machine paired with any conventional sink does. Legacy data stored the
// generation choice in `bopHeatRejection === 'absorption-cchp'`; the C1
// projector below back-maps that to `'absorption-thermal'` so legacy intents
// resolve to the SAME PUE before and after this change (RULE 6 parity).
// ─────────────────────────────────────────────────────────────────────────────

/** The cooling-GENERATION machine (#157), distinct from the cold-end sink. */
export type CoolingGeneration =
  | 'absorption-thermal'
  | 'steam-driven-mechanical'
  | 'vapour-compression-electric'
  | 'unknown';

/** Cooling-generation machines that make facility cooling HEAT-USABLE — i.e.
 *  driven by on-site thermal/steam heat rather than electricity. An electric
 *  vapour-compression chiller is deliberately EXCLUDED (it adds load). */
const HEAT_USABLE_COOLING_GENERATION: ReadonlySet<CoolingGeneration> = new Set([
  'absorption-thermal',
  'steam-driven-mechanical',
]);

/**
 * C1 projector (#157) — derive the cooling-GENERATION machine for an intent.
 *
 * Precedence:
 *   1. An explicit `coolingGeneration` field on the intent (forward-compatible:
 *      read via a duck-typed lookup so this module does not depend on the type
 *      being declared yet — when the field exists it always wins).
 *   2. LEGACY back-compat: a stored `bopHeatRejection === 'absorption-cchp'`
 *      maps to `'absorption-thermal'` — preserving the legacy heat-usable
 *      semantics (and therefore the resolved PUE) exactly.
 *   3. Otherwise `'unknown'` (no credit; conservative).
 *
 * NOTE: this reads the SINK enum ONLY for the legacy back-map. The credit
 * gate itself (`bopIsHeatUsable`) keys off the projected MACHINE, never the
 * sink.
 */
export function projectCoolingGeneration(
  intent: ProjectIntent | undefined,
): CoolingGeneration {
  if (!intent) return 'unknown';

  // 1. Explicit generation field (forward-compatible, duck-typed).
  const explicit = (intent as { coolingGeneration?: unknown }).coolingGeneration;
  if (
    explicit === 'absorption-thermal' ||
    explicit === 'steam-driven-mechanical' ||
    explicit === 'vapour-compression-electric'
  ) {
    return explicit;
  }
  // An explicit non-thermal / unknown machine must not resurrect a legacy
  // absorption marker and earn a waste-heat credit.
  if (explicit === 'unknown' || explicit === 'none' || explicit === 'free-cooling-economiser') {
    return 'unknown';
  }

  // 2. Legacy back-compat: the absorption choice was smuggled into the sink.
  if (intent.bopHeatRejection === 'absorption-cchp') return 'absorption-thermal';

  // 3. Nothing decided.
  return 'unknown';
}

/**
 * Whether a project's cooling GENERATION machine makes facility cooling
 * HEAT-USABLE (#157) — i.e. driven by on-site thermal/steam heat so it offloads
 * cooling-electrical demand. Keys off `projectCoolingGeneration` (the MACHINE),
 * NOT the cold-end sink enum:
 *
 *   - `absorption-thermal` / `steam-driven-mechanical` → heat-usable.
 *   - `vapour-compression-electric` → NOT heat-usable (electric chiller adds
 *     load; earns no credit — correct physics).
 *   - `unknown` / absent — not heat-usable (conservative; the estimate stays at
 *     the base PUE and is flagged an estimate).
 *
 * NOTE: a purely electrical IT loop (e.g. immersion) is handled by the caller —
 * immersion is excluded from the credit even when on-site thermal gen exists,
 * because its cooling-electrical overhead is already minimal.
 */
function bopIsHeatUsable(intent: ProjectIntent | undefined): boolean {
  // PHYSICS-CORRECT (#157 — product decision 2026-06-05): the BTM waste-heat
  // credit requires a THERMALLY-DRIVEN cooling machine that actually reuses
  // waste heat — `absorption-thermal` or `steam-driven-mechanical`. An electric
  // vapour-compression chiller adds electrical load (no credit) and a bare
  // heat-rejection SINK only throws heat away (no reuse → no credit). The credit
  // therefore gates on the cooling-GENERATION MACHINE, never the sink enum.
  //
  // `projectCoolingGeneration` resolves the explicit machine, MIGRATES the
  // legacy `bopHeatRejection === 'absorption-cchp'` marker to
  // `absorption-thermal` (so pre-#157 absorption sites keep their credit + PUE
  // unchanged — RULE 6 parity for that one legacy value), and returns `unknown`
  // otherwise → NO credit. NOTE: this is a DELIBERATE tightening — a site that
  // previously earned the credit via a bare heat-usable sink (e.g. wet tower /
  // dry cooler) + on-site thermal but no declared absorption/steam machine now
  // earns no credit, so its estimated PUE rises. Null-safe (projectCoolingGeneration
  // returns 'unknown' for undefined).
  return HEAT_USABLE_COOLING_GENERATION.has(projectCoolingGeneration(intent));
}

/**
 * The base (pre-credit) config PUE for an IT-side cooling strategy. The
 * granular Vertiv categories (evaporative / direct-to-chip / immersion) are not
 * all present in the 4-value `coolingStrategy` vocabulary, so we additionally
 * read the BoP heat-rejection method to refine the `liquid` case:
 *
 *   - `air`       → CONFIG_PUE_AIR (1.5).
 *   - `liquid`    → CONFIG_PUE_LIQUID (1.3) by default; immersion is signalled
 *     via `bopHeatRejection === 'free-cooling'`? NO — immersion is an IT-side
 *     concept with no `coolingStrategy` slot, so it is exposed through an
 *     explicit `pueValue`/sim path, not inferred here. We keep `liquid` at the
 *     direct-to-chip benchmark and let evaporative wet rejection lower it.
 *   - `hybrid`    → CONFIG_PUE_HYBRID (1.35).
 *   - `undecided` → CONFIG_PUE_UNDECIDED (1.4, conservative).
 *
 * When the BoP side is `wet-cooling-tower` (evaporative rejection) we adopt the
 * evaporative benchmark (1.25) for air/liquid IT loops, since evaporative heat
 * rejection is the dominant PUE driver in that configuration.
 */
function baseConfigPue(intent: ProjectIntent | undefined): number {
  const cooling = intent?.coolingStrategy ?? 'undecided';
  const bop = intent?.bopHeatRejection;

  // Evaporative (wet-tower) heat rejection is the strongest PUE lever for a
  // decided IT loop — adopt the evaporative benchmark for air/liquid loops.
  if (bop === 'wet-cooling-tower' && (cooling === 'air' || cooling === 'liquid')) {
    return CONFIG_PUE_EVAPORATIVE;
  }

  switch (cooling) {
    case 'air':
      return CONFIG_PUE_AIR;
    case 'liquid':
      return CONFIG_PUE_LIQUID;
    case 'hybrid':
      return CONFIG_PUE_HYBRID;
    case 'undecided':
      return CONFIG_PUE_UNDECIDED;
    default: {
      // Exhaustiveness guard — a new coolingStrategy value must be priced here.
      // Fall back conservatively rather than throw (PUE estimate is advisory).
      const _never: never = cooling;
      void _never;
      return CONFIG_PUE_UNDECIDED;
    }
  }
}

/**
 * Estimate a project's PUE from its declared CONFIGURATION (cooling strategy +
 * BoP heat rejection + power mix), used as the resolver fallback when the user
 * has not given an explicit `pueValue` / `pueTarget` and no simulation PUE has
 * been adopted.
 *
 * Two stages:
 *   1. BASE PUE from the IT-side cooling strategy (see `baseConfigPue`).
 *   2. BTM WASTE-HEAT CREDIT — when the power mix has on-site thermal generation
 *      (any phase/source ∈ gas-cc / gas-sc / recip / SMR / large-nuclear) AND the
 *      cooling GENERATION machine is heat-usable (`projectCoolingGeneration` is
 *      absorption-thermal / steam-driven-mechanical — NOT an electric chiller, and
 *      the IT loop is not the already-minimal immersion case), absorption / CCHP
 *      offloads ~half the cooling-electrical overhead:
 *          effectivePUE = 1 + (basePUE − 1) × (1 − WASTE_HEAT_OFFSET)
 *      floored at `CONFIG_PUE_FLOOR`. A grid-only mix earns NO credit.
 *
 * Pure planning arithmetic — no CoolProp / IAPWS boundary. ESTIMATE only; an
 * explicit user value / design target / adopted simulation PUE always wins.
 */
export function resolveConfigPue(intent: ProjectIntent | undefined): number {
  const basePue = baseConfigPue(intent);

  // BTM waste-heat credit: requires BOTH on-site thermal generation AND a
  // heat-usable cooling GENERATION machine (#157). The gate keys off the
  // projected cooling-generation machine (absorption-thermal /
  // steam-driven-mechanical), NOT the cold-end sink — so an electric
  // vapour-compression chiller earns no credit. Grid-only / intermittent-only →
  // no heat, no credit. `unknown` generation → conservative, no credit.
  const heatUsable = bopIsHeatUsable(intent);
  const hasThermalGen = intentHasOnSiteThermalGen(intent);

  if (heatUsable && hasThermalGen) {
    const credited = 1 + (basePue - 1) * (1 - WASTE_HEAT_OFFSET);
    return Math.max(credited, CONFIG_PUE_FLOOR);
  }

  return basePue;
}

/**
 * Provenance of the PUE used in a resolved power stack — lets the UI / reports
 * LABEL where the figure came from (and whether it is an estimate). Ordered by
 * the resolution precedence used in `resolvePueWithSource`:
 *
 *   'simulation'      — adopted per-phase engineering-sim PUE (highest trust).
 *   'user'            — explicit `intent.pueValue`.
 *   'design-target'   — `intent.pueTarget` (a design goal, not yet validated).
 *   'config-estimate' — derived from cooling + power-mix config (an ESTIMATE).
 *   'default'         — the `DEFAULT_PUE` fallback (no config to estimate from).
 */
export type PueSource =
  | 'simulation'
  | 'user'
  | 'design-target'
  | 'config-estimate'
  | 'default';

/**
 * Resolve the PUE to use for an intent (optionally for a specific phase) and
 * report its provenance. Precedence (highest → lowest):
 *
 *   1. ADOPTED SIMULATION PUE — `phase.adoptSimulatedPue && phase.simulatedPueValue`
 *      (user-opted-in engineering-sim result). Per-phase only.
 *   2. USER VALUE — explicit `intent.pueValue`.
 *   3. DESIGN TARGET — `intent.pueTarget`.
 *   4. CONFIG ESTIMATE — `resolveConfigPue(intent)` (always finite/positive).
 *   5. DEFAULT — `DEFAULT_PUE` (only if every prior source was absent/invalid;
 *      in practice the config estimate always supplies a value, so this is a
 *      defensive backstop).
 *
 * Each candidate is validated finite/positive; an invalid value falls through.
 */
export function resolvePueWithSource(
  intent: ProjectIntent | undefined,
  phase?: ProjectPhase,
): { pue: number; source: PueSource } {
  // 1. Adopted simulation PUE (per-phase, user-opted-in).
  if (phase?.adoptSimulatedPue) {
    const simPue = isValidPue(phase.simulatedPueValue) ? phase.simulatedPueValue : null;
    if (simPue !== null) return { pue: simPue, source: 'simulation' };
  }

  // 2. Explicit user value.
  const userPue = isValidPue(intent?.pueValue) ? intent.pueValue : null;
  if (userPue !== null) return { pue: userPue, source: 'user' };

  // 3. Design target.
  const targetPue = isValidPue(intent?.pueTarget) ? intent.pueTarget : null;
  if (targetPue !== null) return { pue: targetPue, source: 'design-target' };

  // 4. Config-derived estimate (always a valid positive number).
  const configPue = positiveOrNull(resolveConfigPue(intent));
  if (configPue !== null) return { pue: configPue, source: 'config-estimate' };

  // 5. Defensive backstop.
  return { pue: DEFAULT_PUE, source: 'default' };
}

/**
 * Which layer of the stack an entered MW figure represents.
 *   - 'facility' — total facility / grid demand (DEFAULT when absent).
 *   - 'it-load'  — server / critical IT load only.
 */
export type PowerBasis = 'it-load' | 'facility';

/** Default basis when an intent / phase does not declare one. */
export const DEFAULT_POWER_BASIS: PowerBasis = 'facility';

/**
 * The fully-resolved power stack for one entered MW figure. Every derived layer
 * is `null` when the input could not be resolved (missing / NaN / ≤ 0), so the
 * absence of a value is always detectable (never silently zero).
 */
export interface PowerStack {
  /** The basis the entered figure was interpreted against. */
  basis: PowerBasis;
  /** PUE used for the IT ↔ facility conversion. */
  pue: number;
  /**
   * Provenance of `pue` — where the figure came from, so the UI / reports can
   * LABEL it (e.g. "config estimate" vs "user-set"). Defaults to 'user' when a
   * raw `pue` is passed directly to `resolvePowerStack`; the intent/phase-level
   * resolvers (`resolveIntentPowerStack`, `phaseFacilityLoadMW`) populate it via
   * the full precedence chain (`resolvePueWithSource`).
   */
  pueSource: PueSource;
  /** Power factor used for the MW → MVA conversion. */
  powerFactor: number;
  /** The raw entered MW (echoed unchanged), or null when absent / invalid. */
  enteredMW: number | null;
  /** Server / critical IT load, MW. facility basis → entered ÷ PUE. */
  itLoadMW: number | null;
  /** Total facility load (IT × PUE), MW. it-load basis → entered × PUE. */
  facilityMW: number | null;
  /** Grid interconnection demand, MW ≈ facility peak. */
  gridMW: number | null;
  /** Apparent power, MVA = facility MW ÷ power factor. */
  apparentMVA: number | null;
  /** Generation that must be PRODUCED to deliver the facility load, MW. */
  generationRequiredMW: number | null;
}

/** A finite, strictly-positive number, else null. */
function positiveOrNull(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * Resolve one entered MW figure into the full power stack.
 *
 * The entered figure is interpreted on `basis` (default 'facility'):
 *   - 'facility' → facilityMW = entered;  itLoadMW = entered ÷ PUE.
 *   - 'it-load'  → itLoadMW   = entered;  facilityMW = entered × PUE.
 *
 * From the facility load we derive:
 *   - gridMW       = facilityMW           (grid interconnection ≈ facility peak)
 *   - apparentMVA  = facilityMW ÷ PF      (transformer / switchgear rating basis)
 *   - generationRequiredMW = facilityMW ÷ (1 − T&D/100)   (delivered → generated)
 *
 * NO DOUBLE-COUNTING: PUE losses (cooling + electrical) are INSIDE the facility
 * meter and are already captured in `facilityMW`. The T&D loss is OUTSIDE the
 * meter (utility side) and only scales the delivered facility load up to the
 * generation that must be produced — the two never compound.
 */
export function resolvePowerStack(input: {
  enteredMW: number | null;
  basis?: PowerBasis;
  pue?: number;
  /**
   * Provenance of `pue`. Defaults to 'user' when a raw `pue` is supplied (a
   * direct caller passed a number) and 'default' when `pue` is absent (the
   * DEFAULT_PUE fallback applies). Intent/phase resolvers pass the resolved
   * source explicitly.
   */
  pueSource?: PueSource;
  powerFactor?: number;
}): PowerStack {
  const basis: PowerBasis = input.basis ?? DEFAULT_POWER_BASIS;
  const pue = isValidPue(input.pue) ? input.pue : DEFAULT_PUE;
  // Provenance: explicit override wins; else infer from whether a raw pue was
  // given (direct numeric input ⇒ 'user', absent ⇒ 'default').
  const pueSource: PueSource =
    isValidPue(input.pue) ? (input.pueSource ?? 'user') : 'default';
  const powerFactor = isValidPowerFactor(input.powerFactor) ? input.powerFactor : DEFAULT_POWER_FACTOR;
  const enteredMW = positiveOrNull(input.enteredMW);

  if (enteredMW === null) {
    return {
      basis,
      pue,
      pueSource,
      powerFactor,
      enteredMW: null,
      itLoadMW: null,
      facilityMW: null,
      gridMW: null,
      apparentMVA: null,
      generationRequiredMW: null,
    };
  }

  const facilityMW = basis === 'facility' ? enteredMW : enteredMW * pue;
  const itLoadMW = basis === 'facility' ? enteredMW / pue : enteredMW;
  // Grid interconnection is sized to the facility peak demand.
  const gridMW = facilityMW;
  // Transformers / switchgear are rated in apparent power.
  const apparentMVA = facilityMW / powerFactor;
  // Delivered facility load → generation that must be produced upstream.
  const generationRequiredMW = facilityMW / (1 - DEFAULT_TD_LOSS_PCT / 100);

  return {
    basis,
    pue,
    pueSource,
    powerFactor,
    enteredMW,
    itLoadMW,
    facilityMW,
    gridMW,
    apparentMVA,
    generationRequiredMW,
  };
}

/**
 * Resolve the power stack for a whole intent — uses the ULTIMATE campus
 * capacity (`effectivePhases(intent).totalCapacityMW`, which under the
 * cumulative convention is the FINAL-phase cumulative value, NOT a sum across
 * phases) as the entered figure, with the intent's basis / PUE / power-factor
 * overrides (defaults applied when absent). This is correct: the whole-project
 * load is the ultimate campus size, exactly the final cumulative.
 */
export function resolveIntentPowerStack(intent: ProjectIntent | undefined): PowerStack {
  const totalMW = intent ? effectivePhases(intent).totalCapacityMW : null;
  // Resolve PUE through the full precedence chain (user → design-target →
  // config-estimate → default) so the stack carries the right value AND its
  // provenance. No `phase` here: the intent-level stack is whole-project, so
  // an adopted per-phase simulation PUE is honoured by `phaseFacilityLoadMW`,
  // not the project rollup.
  const { pue, source } = resolvePueWithSource(intent);
  return resolvePowerStack({
    enteredMW: totalMW,
    basis: intent?.powerBasis,
    pue,
    pueSource: source,
    powerFactor: intent?.powerFactor,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// INVERSE STACK — generation ⇒ how big a datacenter does that support?
//
// `resolvePowerStack` answers "I want N MW of load, how much generation?".
// The max-power study asks the opposite: "this envelope supports G MW of firm
// generation — how large a campus is that?". Same physics, read backwards, and
// it lives HERE so the ratios are never re-derived at a call site.
//
//   facility MW = firm net generation × (1 − T&D/100)      [inverse of ÷(1−T&D)]
//   IT MW       = facility ÷ PUE                            [inverse of ×PUE]
//   apparent    = facility ÷ PF                             [unchanged direction]
//
// CAVEAT (deliberate, surfaced by the study): `solveGenerationImpact` compares
// net generation DIRECTLY against `facilityLoadMW` with no T&D uplift, because
// behind-the-meter generation arguably has no transmission loss at all — while
// `resolvePowerStack` always divides by (1 − T&D). Both readings are defensible.
// We report the T&D-derated figure as the headline so it round-trips exactly
// with `resolvePowerStack`, and expose `facilityMWAtGenerationTerminals`
// alongside it so the ~5% difference is visible rather than hidden.
// ─────────────────────────────────────────────────────────────────────────────

/** `PowerStack` resolved in the inverse direction, from generation to load. */
export interface MaxPowerStack {
  /** The firm net generation the envelope supports, MW (the study's input). */
  firmNetGenerationMW: number;
  pue: number;
  pueSource: PueSource;
  powerFactor: number;
  tdLossPct: number;
  /** Facility load supportable AFTER the T&D derate. The headline figure. */
  facilityMW: number;
  /** Facility load measured at the generation terminals — no T&D derate. */
  facilityMWAtGenerationTerminals: number;
  /** IT / critical load, MW = facility ÷ PUE. */
  itLoadMW: number;
  /** Grid interconnection demand, MW ≈ facility peak. */
  gridMW: number;
  /** Apparent power, MVA = facility ÷ PF. */
  apparentMVA: number;
}

/**
 * Invert the power stack: given firm net generation, resolve the campus it can
 * support. Returns null for a non-positive / non-finite input so callers treat
 * "no answer" uniformly rather than propagating a zero.
 *
 * Round-trip invariant (pinned in `powerStack.maxPower.test.ts`):
 *   resolvePowerStack({ enteredMW: inv.facilityMW }).generationRequiredMW === G
 */
export function resolveMaxPowerStack(input: {
  firmNetGenerationMW: number | null;
  pue?: number;
  pueSource?: PueSource;
  powerFactor?: number;
}): MaxPowerStack | null {
  const firmNetGenerationMW = positiveOrNull(input.firmNetGenerationMW);
  if (firmNetGenerationMW === null) return null;

  const pue = isValidPue(input.pue) ? input.pue : DEFAULT_PUE;
  const pueSource: PueSource =
    isValidPue(input.pue) ? (input.pueSource ?? 'user') : 'default';
  const powerFactor = isValidPowerFactor(input.powerFactor) ? input.powerFactor : DEFAULT_POWER_FACTOR;

  // Delivered facility load ← generation produced upstream.
  const facilityMW = firmNetGenerationMW * (1 - DEFAULT_TD_LOSS_PCT / 100);

  return {
    firmNetGenerationMW,
    pue,
    pueSource,
    powerFactor,
    tdLossPct: DEFAULT_TD_LOSS_PCT,
    facilityMW,
    // BTM reading: no transmission between the plant and the campus.
    facilityMWAtGenerationTerminals: firmNetGenerationMW,
    itLoadMW: facilityMW / pue,
    gridMW: facilityMW,
    apparentMVA: facilityMW / powerFactor,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// BUCKET-DERIVED POWER BANDS
//
// When the user picks a RANGE BUCKET instead of an exact MW, `powerRequirementMW`
// is deliberately cleared (`withPowerRequirementBucket`), so `resolveIntentPowerStack`
// resolves every layer to null and the whole breakdown disappears. That blank
// protects nobody: `firmCapacityBasisFromIntent`, `resolveCapacityMw` (finDecision),
// `effectiveMwFromIntent` (sim sizing) and `bomTemplates` ALREADY project the
// bucket onto `POWER_BUCKET_MIDPOINT_MW` — the user just never sees which figure
// their LCOE and firm-capacity checks ran on.
//
// This section resolves the bucket into a BAND instead: the stack evaluated at
// the band's endpoints plus the documented sizing basis. The bucket has TWO
// possible origins — the top-level `powerRequirement` (bucket-mode intent), or
// the ULTIMATE phase's `capacityBucket` (range-mode final phase, which nulls
// `effectivePhases().totalCapacityMW` so the campus figure is honestly a
// range). Phases win over the top level, mirroring the exact resolver.
// Two rules govern it:
//
//   1. ADDITIVE ONLY. `resolveIntentPowerStack` keeps its null-on-bucket
//      behaviour — eight call sites gate on `stack.facilityMW != null`, and the
//      SED's `buildPowerSemanticsEvidenceFromIntent` relies on that null to
//      suppress a fabricated power section. Nothing here changes it.
//
//   2. A BAND IS NEVER A SCALAR. Every layer is a `PowerBandLayer` STRUCT, not a
//      number, so there is no assignment path from a band into a scalar MW
//      field. That enforces the ingestion engine's "a range never collapses into
//      a scalar" rule structurally rather than by runtime check.
//
// ENDPOINT EVALUATION IS EXACT, not an approximation. For fixed PUE / PF / T&D
// every layer is strictly increasing and LINEAR in the entered MW on both bases
// (all coefficients strictly positive: `positiveOr` guarantees pue, pf > 0 and
// DEFAULT_TD_LOSS_PCT = 5 gives 0.95). So [f(min), f(max)] IS the image of
// [min, max] — evaluating the endpoints loses nothing. `powerStack.band.test.ts`
// pins this with a monotonicity sweep, so a future load-dependent PUE curve
// trips the test instead of silently invalidating the shortcut.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One layer of a bucket-derived band.
 *
 * DELIBERATELY NOT a bare number — see rule 2 above. `low` is null exactly when
 * the bucket is open at the bottom (`under-10mw` starts at 0, which is not a
 * resolvable power figure); `high` is null exactly when the bucket is open at
 * the top (`over-1500mw` / legacy `over-200mw` run to infinity). `sizingBasis`
 * is ALWAYS present — it is the documented projection that finance, BoM and
 * firm-capacity already use.
 */
export interface PowerBandLayer {
  low: number | null;
  high: number | null;
  sizingBasis: number;
  unit: 'MW' | 'MVA';
}

/**
 * The full power stack resolved as a band. Carries the literal discriminator
 * `figureBasis: 'bucket-derived'`, which `PowerStack` has no counterpart for —
 * so the two types are structurally non-interchangeable and a band can never be
 * passed where an exact stack is expected.
 */
export interface PowerStackBand {
  figureBasis: 'bucket-derived';
  bucket: Exclude<PowerBucket, 'undecided'>;
  bucketLabel: string;
  /** True when the bucket has no resolvable lower bound (`under-10mw`). */
  openLow: boolean;
  /** True when the bucket runs to infinity (`over-1500mw`, legacy `over-200mw`). */
  openHigh: boolean;
  enteredBandMW: { min: number; max: number };
  sizingBasisMW: number;
  sizingBasisRule: SizingBasisRule;

  // Shared across all three resolved stacks.
  basis: PowerBasis;
  pue: number;
  pueSource: PueSource;
  powerFactor: number;

  enteredMW: PowerBandLayer;
  itLoadMW: PowerBandLayer;
  facilityMW: PowerBandLayer;
  gridMW: PowerBandLayer;
  apparentMVA: PowerBandLayer;
  generationRequiredMW: PowerBandLayer;

  /** The underlying resolves, for callers that need a whole stack at one end. */
  stacks: { low: PowerStack | null; sizingBasis: PowerStack; high: PowerStack | null };
}

/**
 * How an intent's power figure was established. The preferred entry point for
 * any consumer that renders or cites capacity: the union makes "exact or band?"
 * impossible to skip at a call site.
 */
export type IntentPowerFigure =
  | { kind: 'exact'; stack: PowerStack }
  | { kind: 'band'; band: PowerStackBand }
  | { kind: 'undecided' };

/**
 * The citation discipline every surface must attach when it emits a band.
 *
 * Lives HERE, beside the band types, rather than in one of the two consumers
 * (`reportPrompts.ts` for the LLM directives, `siteEvidence.ts` for the SED
 * prompt block) so the two can never drift — and so neither service module has
 * to import the other.
 */
export const BUCKET_DERIVED_BAND_RULE =
  'BUCKET-DERIVED DISCIPLINE (REQUIRED): this capacity was NOT declared as an exact figure — the operator selected a range bucket. '
  + 'Cite it as the RANGE, with the "bucket-derived" qualifier. Do NOT state any single number from this block as the project\'s capacity, '
  + 'and do NOT present the sizing basis as a declared figure. Where a single figure is unavoidable (a table cell, a chart axis), '
  + 'label it "sizing basis (bucket-derived), not a declared capacity".';

/** Human label for a sizing-basis rule — "midpoint" is only correct for one of
 *  the three, so the label is derived, never hard-coded at a call site. */
export function sizingBasisLabel(rule: SizingBasisRule): string {
  switch (rule) {
    case 'bucket-midpoint':        return 'bucket midpoint';
    case 'bucket-max-open-low':    return 'top of the open band';
    case 'bucket-floor-open-high': return 'floor of the open band';
  }
}

/** Pull one layer out of the three resolved stacks. */
function bandLayer(
  low: PowerStack | null,
  sizingBasis: PowerStack,
  high: PowerStack | null,
  key: 'enteredMW' | 'itLoadMW' | 'facilityMW' | 'gridMW' | 'apparentMVA' | 'generationRequiredMW',
  unit: 'MW' | 'MVA',
): PowerBandLayer {
  return {
    low: low?.[key] ?? null,
    high: high?.[key] ?? null,
    // Non-null by construction: `sizingBasis` is always resolved from a
    // strictly-positive MW, so every layer of that stack is populated.
    sizingBasis: sizingBasis[key] as number,
    unit,
  };
}

/**
 * Resolve a bucket's [min, max] MW range into the full power stack, evaluated at
 * both endpoints plus the sizing basis. Pure; delegates ALL arithmetic to
 * `resolvePowerStack` so the physics has exactly one home.
 *
 * Returns null only when the sizing basis fails to resolve — a malformed input
 * must degrade, never throw, because report views render this inline.
 */
export function resolvePowerStackBand(input: {
  bucket: Exclude<PowerBucket, 'undecided'>;
  range: { min: number; max: number };
  sizingBasisMW: number;
  sizingBasisRule: SizingBasisRule;
  basis?: PowerBasis;
  pue?: number;
  pueSource?: PueSource;
  powerFactor?: number;
}): PowerStackBand | null {
  const shared = {
    basis: input.basis,
    pue: input.pue,
    pueSource: input.pueSource,
    powerFactor: input.powerFactor,
  };

  // `positiveOrNull` rejects 0 and Infinity, which is exactly the open-band
  // test: `under-10mw` starts at 0 and the open-high buckets end at Infinity.
  const openLow = positiveOrNull(input.range.min) === null;
  const openHigh = positiveOrNull(input.range.max) === null;

  const sizingBasisStack = resolvePowerStack({ enteredMW: input.sizingBasisMW, ...shared });
  if (sizingBasisStack.facilityMW == null) return null;

  const lowStack = openLow ? null : resolvePowerStack({ enteredMW: input.range.min, ...shared });
  const highStack = openHigh ? null : resolvePowerStack({ enteredMW: input.range.max, ...shared });

  const layer = (
    key: 'enteredMW' | 'itLoadMW' | 'facilityMW' | 'gridMW' | 'apparentMVA' | 'generationRequiredMW',
    unit: 'MW' | 'MVA',
  ) => bandLayer(lowStack, sizingBasisStack, highStack, key, unit);

  return {
    figureBasis: 'bucket-derived',
    bucket: input.bucket,
    bucketLabel: POWER_BUCKET_LABEL[input.bucket],
    openLow,
    openHigh,
    enteredBandMW: input.range,
    sizingBasisMW: input.sizingBasisMW,
    sizingBasisRule: input.sizingBasisRule,

    basis: sizingBasisStack.basis,
    pue: sizingBasisStack.pue,
    pueSource: sizingBasisStack.pueSource,
    powerFactor: sizingBasisStack.powerFactor,

    enteredMW: layer('enteredMW', 'MW'),
    itLoadMW: layer('itLoadMW', 'MW'),
    facilityMW: layer('facilityMW', 'MW'),
    gridMW: layer('gridMW', 'MW'),
    // Apparent power is the ONLY MVA layer. Hard-coding the unit per layer is
    // what stops the recurring defect `normalizePowerBasisBreakdown` repairs,
    // where an MVA figure leaks into the generation-required MW field.
    apparentMVA: layer('apparentMVA', 'MVA'),
    generationRequiredMW: layer('generationRequiredMW', 'MW'),

    stacks: { low: lowStack, sizingBasis: sizingBasisStack, high: highStack },
  };
}

/**
 * Resolve an intent's bucket into a power-stack band — STRICT FALLBACK.
 *
 * Returns null (never a band) whenever an exact figure exists, so the band can
 * never shadow a declared number. Resolution order matters:
 *
 *   0. RANGE-mode ULTIMATE phase (`effectivePhases().ultimateCapacityBucket`)
 *      ⇒ the band derives from THAT bucket — the campus figure IS the final
 *      phase's cumulative range. Phases WIN over the top level here, exactly
 *      as they do for exact figures in `resolveIntentPowerStack`: this branch
 *      deliberately never consults `effectivePower(intent)`, so a stale
 *      top-level exact MW (mid-edit, before reconcile-on-save syncs the
 *      bucket) cannot block the phase-derived band.
 *   1. Any EXACT campus figure (`totalCapacityMW`, phase-carried or top-level)
 *      ⇒ null. A bucket-mode intent that ALSO has an exact-ultimate phase
 *      already resolves a real stack today; the band exists only for the case
 *      where nothing else does.
 *   2. `undecided` / absent bucket ⇒ null.
 *   3. Otherwise: the top-level bucket's range + its documented sizing basis.
 *
 * PUE / PF resolve through the SAME `resolvePueWithSource` call that
 * `resolveIntentPowerStack` makes, so band PUE and exact PUE can never disagree
 * (campus-level chain for both branches — mirroring how the exact resolver
 * feeds the phases' ultimate through the campus-level PUE).
 */
export function resolveIntentPowerStackBand(
  intent: ProjectIntent | undefined | null,
): PowerStackBand | null {
  if (!intent) return null;
  const view = effectivePhases(intent);

  // (0) Range-mode ultimate phase — the campus band comes from the phase.
  if (view.isPhased && view.totalCapacityMW == null && view.ultimateCapacityBucket != null) {
    const bucket = view.ultimateCapacityBucket;
    const range = bucketMwRange(bucket);
    const sizingBasisMW = bucketMidpointMw(bucket);
    if (range == null || sizingBasisMW == null) return null;
    const { pue, source } = resolvePueWithSource(intent);
    return resolvePowerStackBand({
      bucket,
      range,
      sizingBasisMW,
      sizingBasisRule: SIZING_BASIS_RULE[bucket],
      basis: intent.powerBasis,
      pue,
      pueSource: source,
      powerFactor: intent.powerFactor,
    });
  }

  // (1) Never shadow an exact figure — including one carried by phases.
  if (view.totalCapacityMW != null) return null;
  // When an earlier phase carries an exact MW while the final phase is TBD
  // (no ultimateCapacityBucket), do not fall back to a stale top-level bucket.
  if (view.isPhased && view.knownCapacityFloorMW != null) return null;

  const ep = effectivePower(intent);
  // Defensive: an exact MW must never produce a degenerate {mw, mw} band.
  if (ep.isExact) return null;

  const range = effectivePowerMwRange(ep);
  if (range == null) return null; // (2) undecided
  if (ep.bucket === 'undecided') return null;

  const sizingBasisMW = bucketMidpointMw(ep.bucket);
  if (sizingBasisMW == null) return null;

  const { pue, source } = resolvePueWithSource(intent);
  return resolvePowerStackBand({
    bucket: ep.bucket,
    range,
    sizingBasisMW,
    sizingBasisRule: SIZING_BASIS_RULE[ep.bucket],
    basis: intent.powerBasis,
    pue,
    pueSource: source,
    powerFactor: intent.powerFactor,
  });
}

/**
 * The preferred entry point: resolve an intent's power figure as a discriminated
 * union so callers cannot forget the bucket case. Exact always wins.
 */
export function resolveIntentPowerFigure(
  intent: ProjectIntent | undefined | null,
): IntentPowerFigure {
  const stack = resolveIntentPowerStack(intent ?? undefined);
  if (stack.facilityMW != null) return { kind: 'exact', stack };
  const band = resolveIntentPowerStackBand(intent);
  if (band) return { kind: 'band', band };
  return { kind: 'undecided' };
}

/**
 * The ONLY sanctioned way to render a band layer as text. Never emits a bare
 * scalar, so a band cannot be mistaken for a declared figure in any surface:
 *
 *   closed     → "231 – 577 MW"
 *   open-low   → "up to 231 MW"
 *   open-high  → "≥ 1731 MW"
 */
export function formatPowerBandLayer(
  layer: PowerBandLayer,
  fmt: (n: number) => string = (n) => String(Math.round(n)),
): string {
  const { low, high, unit } = layer;
  if (low != null && high != null) return `${fmt(low)} – ${fmt(high)} ${unit}`;
  if (low == null && high != null) return `up to ${fmt(high)} ${unit}`;
  if (low != null && high == null) return `≥ ${fmt(low)} ${unit}`;
  // Unreachable for every bucket in the ladder (each has at least one finite
  // bound), but degrade to the sizing basis rather than emit an empty string.
  return `~${fmt(layer.sizingBasis)} ${unit} (sizing basis)`;
}

/**
 * The MW range a narrative figure may cite without counting as capacity drift.
 *
 * Unions the ENTERED band with the FACILITY band, because reports legitimately
 * quote either layer: on an it-load basis a "200–500 MW" bucket has an entered
 * band of 200–500 but a facility band of 260–650 at PUE 1.3, and a narrative
 * citing 600 MW facility is correct, not drift.
 *
 * Returns null when the intent carries no capacity signal at all, preserving the
 * existing "skip the divergence check entirely" semantics.
 */
export function capacityDriftRangeFromIntent(
  intent: ProjectIntent | undefined | null,
): { min: number; max: number } | null {
  const figure = resolveIntentPowerFigure(intent);
  if (figure.kind === 'undecided') return null;
  if (figure.kind === 'exact') {
    const { enteredMW, facilityMW } = figure.stack;
    if (enteredMW == null || facilityMW == null) return null;
    return { min: Math.min(enteredMW, facilityMW), max: Math.max(enteredMW, facilityMW) };
  }
  const { enteredMW, facilityMW } = figure.band;
  // Open ends widen to the degenerate bounds so the guard short-circuits rather
  // than comparing against a fabricated edge.
  const min = Math.min(enteredMW.low ?? 0, facilityMW.low ?? 0);
  const max = Math.max(enteredMW.high ?? Number.POSITIVE_INFINITY, facilityMW.high ?? Number.POSITIVE_INFINITY);
  return { min, max };
}

/**
 * The FACILITY load for a single phase, MW — `phase.capacityMW` resolved through
 * the intent's basis + PUE. This is the firm 24/7 load that generation must
 * serve for that phase. Under the CUMULATIVE convention `phase.capacityMW` is
 * the cumulative campus size at that phase, which is exactly the load to serve
 * at that point — so it is used AS-IS (cumulative, NOT the per-phase increment):
 *   - facility basis → phase.capacityMW as-is.
 *   - it-load basis  → phase.capacityMW × PUE.
 * Returns null when the phase has no usable capacity.
 */
export function phaseFacilityLoadMW(
  phase: ProjectPhase,
  intent: ProjectIntent | undefined,
): number | null {
  return resolvePhasePowerStack(phase, intent).facilityMW;
}

/**
 * The full power stack for a SINGLE phase — `phase.capacityMW` resolved through
 * the intent's basis + power-factor and the PER-PHASE PUE.
 *
 * The phase PUE is resolved with the full precedence chain (`resolvePueWithSource`)
 * passing this `phase`, so an ADOPTED per-phase simulation PUE
 * (`phase.adoptSimulatedPue && phase.simulatedPueValue`) overrides the intent's
 * user value / target / config estimate FOR THIS PHASE — and the returned
 * `pueSource` labels which one was used. A per-phase `bopHeatRejection` /
 * `coolingStrategy` override is folded into the config estimate via a phase-scoped
 * intent view, so the waste-heat credit reflects the phase's own configuration.
 */
export function resolvePhasePowerStack(
  phase: ProjectPhase,
  intent: ProjectIntent | undefined,
): PowerStack {
  // Only this phase can supply waste heat to its cooling plant. A future
  // thermal phase must not reduce today's grid-only phase load.
  const phaseScopedIntent: ProjectIntent | undefined = intent
    ? {
        ...intent,
        phases: [phase],
        // IT-side cooling loop is a SITE-LEVEL decision only (per-phase
        // override removed from the form) → always inherit intent.coolingStrategy.
        coolingStrategy: intent.coolingStrategy,
        bopHeatRejection: phase.bopHeatRejection ?? intent.bopHeatRejection,
        coolingGeneration: phase.coolingGeneration ?? intent.coolingGeneration,
      }
    : undefined;

  const { pue, source } = resolvePueWithSource(phaseScopedIntent, phase);
  return resolvePowerStack({
    enteredMW: phase.capacityMW,
    basis: intent?.powerBasis,
    pue,
    pueSource: source,
    powerFactor: intent?.powerFactor,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// String-typed power-basis breakdown sanitizer (R-FIN-PHASE-D).
//
// The report's `powerStrategyContext.powerBasisBreakdown` is filled with prose
// strings (e.g. "100 MW", "111 MVA"). A recurring generation defect copies the
// APPARENT-POWER MVA value into the `generationRequiredMW` field — but
// generation required is REAL power (= facility MW ÷ (1 − T&D)), not apparent
// power (= facility MW ÷ PF). This sanitizer corrects that at the render
// boundary so MVA never leaks into the MW field, regardless of upstream source.
// ─────────────────────────────────────────────────────────────────────────────

export interface StringPowerBasisBreakdown {
  basis?: string;
  itLoadMW?: string;
  facilityMW?: string;
  apparentMVA?: string;
  generationRequiredMW?: string;
  pue?: string;
  powerFactor?: string;
  notes?: string;
}

/** Extract the leading numeric magnitude from a "123.4 MW"-style string. */
function magnitudeFromValueString(v: string | undefined): number | null {
  if (!v) return null;
  const m = v.match(/-?\d+(?:\.\d+)?/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  return Number.isFinite(n) ? n : null;
}

/** Round to 1 decimal for display, dropping a trailing ".0". */
function fmtMw(n: number): string {
  const r = Math.round(n * 10) / 10;
  return `${Number.isInteger(r) ? r : r} MW`;
}

/**
 * Return a corrected copy of a string-typed power-basis breakdown.
 *
 * Fixes the "MVA copied into the MW field" defect: if `generationRequiredMW`
 * is absent, carries an MVA unit, or numerically equals `apparentMVA` (the
 * tell-tale of the copy bug), it is recomputed from the facility MW as real
 * power (facility ÷ (1 − T&D)). `apparentMVA` is preserved untouched. Returns
 * the input unchanged when there is no facility MW to recompute from.
 */
export function normalizePowerBasisBreakdown<T extends StringPowerBasisBreakdown>(
  pbb: T | null | undefined,
): T | null | undefined {
  if (!pbb) return pbb;

  const facilityMw = magnitudeFromValueString(pbb.facilityMW);
  const apparentMva = magnitudeFromValueString(pbb.apparentMVA);
  const genRaw = pbb.generationRequiredMW?.trim();
  const genMag = magnitudeFromValueString(pbb.generationRequiredMW);

  const looksLikeMva = !!genRaw && /\bMVA\b/i.test(genRaw);
  const equalsApparent =
    genMag != null && apparentMva != null && Math.abs(genMag - apparentMva) < 0.05;
  const missing = !genRaw;

  // Only correct when we can recompute real power from a facility MW figure.
  if ((looksLikeMva || equalsApparent || missing) && facilityMw != null) {
    const generationRequiredMW = facilityMw / (1 - DEFAULT_TD_LOSS_PCT / 100);
    return { ...pbb, generationRequiredMW: fmtMw(generationRequiredMW) };
  }
  return pbb;
}
